/**
 * marginCoverage — the Margin surface's header numbers: the four cards, and the population
 * behind them as figures that add up.
 *
 * WHY THIS EXISTS. The card row shows billed value and a cost, and a reader subtracts them.
 * That subtraction is wrong wherever coverage < 100%: GM is taken on the COSTED value, not on
 * the billed value, because a line with revenue and no cost would otherwise read as 100%
 * margin. The cards were right and unreadable — the arithmetic only closed in a 9.5px note.
 * This model names every part, so the panel above the cards can show the whole £ split once.
 *
 * ⭐ AND THE TWO "LENSES" ARE NOW STATEMENTS, NOT A CONTROL (24 Aug 2026). They never were a
 * choice: `cost_basis` is a property of the row and it does not vary inside a site — Kirkstall
 * reads job_actual on 10,828 of 10,828 rows, Oracle quote_estimated on 1,414 of 1,414. So the
 * toggle did nothing at either site, and at Group it showed one entity's cost and hid the
 * other's. The server now serves ONE cost with a lens per entity, and this model reads it.
 *
 * WHY ONE COST IS DEFENSIBLE NOW, and was not before. Oracle's quote used to price conversion
 * at a rate Red Ant also charges out (CostRate == ChargeRate on 47 of 47 work centres), so
 * adding it to Kirkstall's job cost added a cost of sales to a sell price. The de-load to the
 * direct rate read from WKCRates fixed the KIND, not just the size: Kirkstall now bridges Sage
 * at +0.6pt and Oracle at −4.7pt, and the blend at +1.8pt. It is still a measurement beside an
 * estimate, which is exactly what the lens tiles keep on the page.
 */

import type { BreakdownTiles } from './orderBook'
import type { MarginTotals } from '../types/margin'
import type { Site } from '../types/metrics'

/** One entity's share of the billed value, and what that entity's cost actually IS. */
export interface CoverageLens {
  entity: string
  /** What kind of number this cost is — the distinction the panel exists to keep. */
  kind: string
  basis: string
  costedValue: number
  costedLines: number
  pct: number
  cost: number
  gm: number
  gmPct: number | null
}

export interface Coverage {
  value: number
  lineCount: number
  lenses: CoverageLens[]
  /** Billed value that NOTHING prices, and the share of value it is. Zero is a real answer
   * (everything is costed) and renders as such; the tile is drawn either way. */
  uncosted: number
  uncostedPct: number
  uncostedLines: number
}

/** What the mart's `cost_basis` means, in the words the surface uses for it. An unknown basis
 * is echoed rather than guessed at — a new value in the mart must read as unfamiliar, never
 * be silently relabelled as one of these two. */
const KIND: Record<string, string> = {
  job_actual: 'an outcome — what the job took',
  quote_estimated: 'an estimate — the quote, de-loaded to the direct rate',
}

/** The panel's figures, or null when this payload cannot support them honestly — no value to
 * divide by, or nothing costed at all. */
export function coverage(totals: MarginTotals | null): Coverage | null {
  if (!totals || totals.value <= 0) return null
  const lenses: CoverageLens[] = []
  // `?? []` and not a bare read: a payload from before this field existed must render as
  // "no split to state", never crash the header block the whole surface hangs off.
  for (const lens of totals.lenses ?? []) {
    if (lens.costed_value === null || lens.costed_lines === null) continue
    if (lens.cost === null || lens.gm === null || lens.costed_value <= 0) continue
    lenses.push({
      entity: lens.entity,
      kind: KIND[lens.basis] ?? lens.basis,
      basis: lens.basis,
      costedValue: lens.costed_value,
      costedLines: lens.costed_lines,
      pct: (100 * lens.costed_value) / totals.value,
      cost: lens.cost,
      gm: lens.gm,
      gmPct: lens.gm_pct,
    })
  }
  // The residue is the server's own, not a re-derivation: the lenses roll the SAME cost column
  // over disjoint rows, so what they do not price is what nothing prices.
  const uncosted = totals.value - (totals.cost.costed_value ?? 0)
  const uncostedLines = totals.line_count - (totals.cost.costed_lines ?? 0)
  return {
    value: totals.value,
    lineCount: totals.line_count,
    lenses,
    uncosted,
    uncostedPct: (100 * uncosted) / totals.value,
    uncostedLines,
  }
}

/** The four header cards. `value` is every billed line; cost/GM are costed-only, so they
 * match the per-row GM% and the chart's GM% line. There is no `offLens` — one cost leaves
 * nothing out except the uncosted residue, and the band above the cards names that. */
export function tiles(totals: MarginTotals): BreakdownTiles {
  const c = totals.cost
  return {
    value: totals.value,
    cost: c.cost,
    gm: c.gm,
    gmPct: c.gm_pct,
    coveragePct: c.coverage_pct,
    lineCount: totals.line_count,
    costedValue: c.costed_value,
    offLens: null,
  }
}

/** The trade between the two Group entities, ready to state. */
export interface Intercompany {
  value: number
  /** Its share of this node's billed value. */
  pct: number
  lines: number
}

/**
 * The part of the Group figure that is one site selling to the other, or null.
 *
 * ⭐ IT IS COUNTED TWICE AND NOBODY SAID SO (Mike, 24 Aug 2026). Sage does not eliminate —
 * `Group − Kirkstall − Oracle = 0.00` on all 16 cells of 2026 — and our Group is a sum for
 * the same reason. The amount was measured on 20 Aug, written into a spec, and shown on no
 * screen. A caveat a reader cannot see is not a caveat.
 *
 * ⚠ GROUP ONLY, and that is not squeamishness. At an entity the trade is real revenue: Oracle
 * genuinely sold that work and genuinely earned a margin on it. The double-count exists only
 * where the two sites are ADDED. A warning printed where it does not apply teaches a reader
 * to skip the one that does.
 *
 * ⚠ NEVER SUBTRACTED. Elimination is an accounting-policy decision for Kaleidex, and it would
 * make our Group figure disagree with Power BI's. The surface states the amount and stops.
 */
export function intercompany(totals: MarginTotals | null, site: Site): Intercompany | null {
  const trade = totals?.intercompany
  if (!trade || !totals || site !== 'Group' || totals.value <= 0) return null
  return { value: trade.value, pct: (100 * trade.value) / totals.value, lines: trade.line_count }
}

const SHORT: Record<string, string> = { job_actual: 'measured', quote_estimated: 'estimated' }

/** What the Cost card should say it is. One entity → that entity's basis, named in full. Both
 * → the blend, said AS a blend, because a reader who takes the Group figure for a measured
 * one has been misled by a card that stayed silent. */
export function costNote(totals: MarginTotals): string {
  const lenses = totals.lenses ?? []
  if (lenses.length === 0) return 'no cost basis'
  if (lenses.length === 1) return `${lenses[0].entity} · ${KIND[lenses[0].basis] ?? lenses[0].basis}`
  return lenses.map((lens) => `${lens.entity} ${SHORT[lens.basis] ?? lens.basis}`).join(' + ')
}
